<template>
    <Card title="Program Dashboard">
        <b-row align-v="end">
            <b-col cols="6">
                <SearchComponent v-model="program"
                                 label="Program"
                                 search-u-r-l="/Program"
                                 prepopulate
                                 clearable/>
            </b-col>
            <b-col>
                <b-form-group label="From Date">
                    <el-date-picker v-model="fromDate"
                                    format="MM/dd/yyyy"
                                    type="date"></el-date-picker>
                </b-form-group>
            </b-col>
            <b-col>
                <b-form-group label="To Date">
                    <el-date-picker v-model="toDate"
                                    format="MM/dd/yyyy"
                                    type="date"></el-date-picker>
                </b-form-group>
            </b-col>
        </b-row>
        <b-row>
            <b-col>
                <b-table id="scripts-by-program-table"
                         ref="scripts-by-program-table"
                         no-provider-sorting
                         sort-icon-left
                         show-empty
                         :empty-text="emptyText"
                         :empty-filtered-text="emptyText"
                         :tbody-tr-class="colorCodeTable"
                         :items="tableProvider"
                         :filter="tableFilter"
                         :fields="fields"
                         api-url="/Prescription/ByProgram">
                    <template #table-busy>
                        <div class="text-center text-danger my-2">
                            <b-spinner class="align-middle"></b-spinner>
                            <strong>Loading...</strong>
                        </div>
                    </template>
                    <template #cell(rxId)="data">
                        <span v-if="isRefillable(data.item)">
                            {{data.item.rxId}}
                        </span>
                        <b v-else v-b-tooltip="statusName(data.item)" style="cursor: pointer">
                            {{data.item.rxId}}
                        </b>
                    </template>
                    <template #cell(edit)="data">
                        <router-link :to="rxDetailsRoute(data.item)">
                            <b-icon
                                v-b-tooltip="'Edit'"
                                icon="pencil-square"
                            ></b-icon>
                        </router-link>
                    </template>

                </b-table>
            </b-col>
        </b-row>
    </Card>
</template>

<script lang="ts">
    import { Vue, Component } from "vue-property-decorator";
    import { DatePicker } from "element-ui";
    import SearchComponent from "@/components/SearchComponent.vue";
    import { Program } from "@/models/Program";
    import { Patient } from "@/models/Patient";
    import { Prescriber } from "@/models/Prescriber";
    import moment from "moment";
    import { BvTableCtxObject } from "bootstrap-vue";
    import axios, { AxiosError, AxiosRequestConfig } from "axios";
    import { Prescription, RefillStatusCode } from "@/models/Prescription";
    import { NotificationOptions } from "@/util/NotificationOptionsPresets";
    import numeral from "numeral";

    @Component({
        name: "ProgramDashboard",
        components: {
            [DatePicker.name]: DatePicker,
            SearchComponent
        },
    })
    export default class ProgramDashboard extends Vue {
        private program: Program = new Program();

        private toDate: any = moment().toDate();
        private fromDate: any = moment()
            .subtract(7, "days")
            .toDate();

        private errorMessage = "";

        private fields = [
            { key: "productNameShort", label: "Drug", sortable: true },
            { key: "patient", class: "patient-col", formatter: this.patientFormatter, sortable: true, },
            { key: "storeName", label: "Store", sortable: true },
            { key: "rxId", sortable: true },
            { key: "daySupply", label: "DS", sortable: true },
            { key: "dispensed", sortable: true },
            { key: "writtenDate", formatter: this.dateFormatter, sortable: true },
            { key: "fillDate", formatter: this.dateFormatter, sortable: true },
            { key: "orderCreateDate", label: "Order Created", formatter: this.dateFormatter, sortable: true, },
            { key: "patientPay", formatter: this.moneyFormatter, sortable: true },
            { key: "programName", label: "Program", sortable: true },
            { key: "prescriber", formatter: this.prescriberFormatter, sortable: true },
            { key: "state" },
            { key: "orderStatus", label: "Order Status" },
            { key: "edit", label: "" }
        ];

        get emptyText(): string {
            if (this.program.id == 0) return "You must select a program";
            return `No Prescriptions for ${this.program.name} between ${moment(this.fromDate).format("L")} and ${moment(this.toDate).format("L")}`;
        }

        get tableFilter(): AxiosRequestConfig {
            return {
                params: {
                    programId: this.program.id,
                    fromDate: this.fromDate,
                    toDate: this.toDate
                }
            };
        }

        prescriberFormatter(dr: Prescriber): string {
            dr = new Prescriber(dr);
            return dr.displayNamesForPerson();
        }

        patientFormatter(pt: Patient): string {
            pt = new Patient(pt);
            return pt.displayNamesForPerson();
        }

        dateFormatter(d: any): string {
            if (d == null) return "";
            return moment(d).format("L");
        }

        moneyFormatter(d: number) {
            if (d == null) return "";
            return numeral(d).format("$0,0.00");
        }

        async tableProvider(ctx: BvTableCtxObject) {
            if (this.program.id === 0) return [];
            const uri = ctx.apiUrl + "";
            const config = ctx.filter as AxiosRequestConfig;
            try {
                const resp = await axios.get<Prescription[]>(uri, config);
                return resp.data.map(rx => new Prescription(rx));
            } catch (err) {
                const error = err as AxiosError;
                this.$notification(NotificationOptions.error(err));
                this.errorMessage = (error?.response?.data as any)?.toString() ?? "";
                return [];
            }
        }

        rxDetailsRoute(item: Prescription): any {
            return {
                name: "PrescriptionDetails",
                params: {
                    storeID: item.storeID,
                    rxNumber: item.rxNumber,
                    rfNumber: item.rfNumber
                }
            };
        }

        colorCodeTable(rxRow: Prescription) {
            const rx = Object.assign(new Prescription(), rxRow);
            if (rx.status == RefillStatusCode.TransferOut) return "table-info";
            else if (rx.status == RefillStatusCode.Void) return "table-dark";
            else if (!rx.isRefillableStatus) return "table-danger";
        }

        isRefillable(rxRow: Prescription) {
            const rx = Object.assign(new Prescription(), rxRow);
            return rx.isRefillableStatus;
        }

        statusName(rxRow: Prescription) {
            const rx = Object.assign(new Prescription(), rxRow);
            return rx.statusName;
        }

    }
</script>

<style scoped>

</style>
